import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function ImportReview() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = async () => { const { data } = await api.get("/import/review"); setRows(data); };
  useEffect(() => { load(); }, []);

  const resolve = async (id, action) => {
    try { await api.post(`/import/review/${id}/resolve`, null, { params: { action } }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reimport = async () => {
    if (!confirm("Re-import the workbook? This clears existing imported assets, suppliers, compliance, standards and re-populates from the spreadsheet.")) return;
    setBusy(true);
    try { const { data } = await api.post("/import/run"); toast.success(`Imported: ${data.assets} assets, ${data.maintenance_tasks + (data.grounds_tasks||0)} tasks, ${data.suppliers} suppliers`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="import-review-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Import validation</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Import Review</h1>
          <p className="text-sm text-slate-600 mt-1">Rows the importer could not confidently place. Nothing is silently discarded.</p>
        </div>
        {user?.role === "admin" && (
          <Button variant="outline" onClick={reimport} disabled={busy} data-testid="reimport-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy?"animate-spin":""}`}/>{busy?"Importing…":"Re-import workbook"}
          </Button>
        )}
      </div>

      <Card className="border-slate-200"><CardContent className="p-4">
        {rows.length===0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            <div className="inline-flex items-center gap-2 text-slate-600"><AlertTriangle className="w-4 h-4 text-emerald-600"/>No rows need review — the last import mapped cleanly.</div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((r)=>(
              <li key={r.id} className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500">{r.source_sheet} · row {r.row_number}</div>
                    <div className="text-sm font-medium text-slate-900">{r.issue}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{r.entity_type}</Badge>
                    <Button size="sm" variant="outline" onClick={()=>resolve(r.id, "accepted")} data-testid={`resolve-accept-${r.id}`}>Accept</Button>
                    <Button size="sm" variant="ghost" onClick={()=>resolve(r.id, "rejected")} data-testid={`resolve-reject-${r.id}`}>Reject</Button>
                  </div>
                </div>
                <pre className="mt-2 text-[11px] bg-slate-50 border border-slate-100 rounded p-2 overflow-x-auto">{JSON.stringify(r.raw_data, null, 2)}</pre>
              </li>
            ))}
          </ul>
        )}
      </CardContent></Card>
    </div>
  );
}
